import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';

import {basePath} from '@atmi/core/injection';
import {CommonCard, ResponsePaginateSuccess, ResponseSuccess, SourceBibliographic} from '@atmi/core/models';
import {generateIndexQueryString, generatePlainQueryParamsFromObject, IndexQueryStringType} from '@atmi/core/utils';
import {MediaService} from '@sim/media';
import {addSourceBib, deleteSourceBib, setSourceBib, updateSourceBib} from '@src/app/store/source-bib.repository';
import {firstValueFrom} from 'rxjs';

import {first, map, tap} from 'rxjs/operators';

import {KeyGeneratorService} from './key-generator.service';

export type ErrorResponseObj = { error: any, id: string; };
export const isErrored = (data: any): boolean => typeof data === 'object' && data && Reflect.has(data, 'error');


@Injectable()
export class SourceBibApiService {

  private readonly url = `${this.baseUrl}/documenti-bib`;

  constructor(
    @Inject(basePath) private readonly baseUrl: string,
    private readonly http: HttpClient,
    private readonly mediaApi: MediaService,
    private readonly keyGeneratorService: KeyGeneratorService,
  ) {
  }

  getAll() {
    return this.http.get<ResponsePaginateSuccess<SourceBibliographic[]>>(this.url, {})
      .pipe(
        map((res) => ({data: res.data, count: res.count})),
        tap((res) => {
          setSourceBib(res.data);
          return res;
        }),
        first(),
      );
  }

  index(queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);

    return this.http.get<ResponsePaginateSuccess<SourceBibliographic[]>>(this.url, {params}).pipe(
      map((res) => ({data: res.data, count: res.count})),
      first(),
    );
  }

  get(id: string) {
    return this.http.get<ResponseSuccess<SourceBibliographic>>(`${this.url}/${id}`).pipe(
      map((res) => res.data),
      first(),
    );
  }

  getManyWithFile(ids: string[]): Promise<SourceBibliographic | ErrorResponseObj>[] {
    return ids.map(async (id) => {
      try {
        const source = await firstValueFrom(this.get(id).pipe(first()));
        if (source?.immagine) {
          const media = await firstValueFrom(this.mediaApi.get(source.immagine).pipe(first()));
          source.immagine = media.previewUrl;
        }

        return source;
      } catch (err) {
        return {
          error: err,
          id
        };
      }
    });
  }

  /*@TODO get many with files base64 thumbnail in previewUrl!!!*/
  async getMany(ids: string[]) {
    return await Promise.all(ids.map(async (id) => firstValueFrom(this.get(id).pipe(first()))));
  }

  post(sourceBib: SourceBibliographic) {
    return this.http.post<ResponseSuccess<SourceBibliographic>>(this.url, sourceBib).pipe(
      map((res) => res.data),
      tap((sourceBib) => {
        addSourceBib(sourceBib);
        return sourceBib;
      }),
      first(),
    );
  }

  put(sourceBib: SourceBibliographic) {
    return this.http.put<ResponseSuccess<SourceBibliographic>>(`${this.url}/${sourceBib.id}`, sourceBib).pipe(
      map((res) => res.data),
      tap((sB) => {
        updateSourceBib(sB.id, sB);
        return sB;
      }),
      first(),
    );
  }

  remove(id: string) {
    return this.http.delete(`${this.url}/${id
    }`).pipe(
      tap((res) => {
        deleteSourceBib(id);
        return res;
      }),
      first());
  }

  /*
  * get beni correlati al documento bibliografico*/
  getCards(id: string, queryString?: IndexQueryStringType) {
    const params = generateIndexQueryString(queryString);
    const url = `${this.url}/${id}/beni`;

    return this.http.get<ResponsePaginateSuccess<CommonCard[]>>(url, {params}).pipe(
      map((res) => ({data: res.data, count: res.count})),
      first(),
    );
  }


  exportBib(id: string, template: string = 'default', format: string = 'pdf') {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();

    let url = `${this.url}/${id}/export?template=${template}&format=${format}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, {responseType: 'blob'});
  }

  exportList(
    template: string = 'default',
    queryString: IndexQueryStringType,
    selection?: string[],
  ) {
    const key = this.keyGeneratorService.getBinaryKeyGenerator();
    const params = generatePlainQueryParamsFromObject({...queryString, selection});

    let url = `${this.url}/export?template=${template}&${params}`;
    if (key) {
      url += `&key=${key}`;
    }

    return this.http.get(url, {responseType: 'blob'});
  }
}
